import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@/api/client';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Textarea } from '@/components/atoms/Textarea';
import { Skeleton } from '@/components/atoms/Skeleton';
import { timeAgo } from '@/lib/format';
import { Check, Send, Trash2, Mail, MailOpen, PenSquare, X, Search, Inbox } from 'lucide-react';
import { EmptyState } from '@/components/atoms/EmptyState';
import { cn } from '@/lib/cn';
import { toast } from '@/store/toastStore';

interface InboxMsg {
  id: number;
  sender_id: string;
  sender_nickname: string;
  sender_image: string | null;
  sender_level: number;
  content: string;
  is_read: boolean;
  created_at: string;
}

interface SentMsg {
  id: number;
  receiver_id: string;
  receiver_nickname: string;
  receiver_image: string | null;
  receiver_level: number;
  content: string;
  is_read: boolean;
  created_at: string;
}

type Tab = 'inbox' | 'sent';

interface UserResult {
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
}

export function MessagesPage() {
  const [searchParams] = useSearchParams();
  const toUserId = searchParams.get('to');
  const toNickname = searchParams.get('nickname');

  const [tab, setTab] = useState<Tab>('inbox');
  const [composeOpen, setComposeOpen] = useState(!!toUserId);
  const [receiver, setReceiver] = useState<UserResult | null>(
    toUserId
      ? { user_id: toUserId, nickname: toNickname ?? '', profile_image_url: null, level: 0 }
      : null,
  );
  const [userQuery, setUserQuery] = useState('');
  const [debouncedUser, setDebouncedUser] = useState('');
  const [msgContent, setMsgContent] = useState('');
  const [userDropOpen, setUserDropOpen] = useState(false);
  const userWrapRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // 유저 검색 debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [userQuery]);

  // 외부 클릭
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (userWrapRef.current && !userWrapRef.current.contains(e.target as Node))
        setUserDropOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // 유저 검색
  const { data: userResults } = useQuery({
    queryKey: ['users', 'search', debouncedUser],
    queryFn: () =>
      get<{ list: UserResult[] }>(
        `/users/search?q=${encodeURIComponent(debouncedUser)}&limit=5`,
      ),
    enabled: userDropOpen && debouncedUser.length > 0,
    staleTime: 30_000,
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      post('/messages', { receiverId: receiver!.user_id, content: msgContent }),
    onSuccess: () => {
      setMsgContent('');
      setReceiver(null);
      setUserQuery('');
      setComposeOpen(false);
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '전송 실패'),
  });

  const inboxQuery = useQuery({
    queryKey: ['messages', 'inbox'],
    queryFn: () => get<{ list: InboxMsg[] }>('/messages'),
    enabled: tab === 'inbox',
  });

  const sentQuery = useQuery({
    queryKey: ['messages', 'sent'],
    queryFn: () => get<{ list: SentMsg[] }>('/messages/sent'),
    enabled: tab === 'sent',
  });

  const readAllMutation = useMutation({
    mutationFn: () => put('/messages/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => put(`/messages/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['messages'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => del(`/messages/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['messages'] }),
  });

  const isLoading =
    (tab === 'inbox' && inboxQuery.isLoading) ||
    (tab === 'sent' && sentQuery.isLoading);

  const inboxList = inboxQuery.data?.list ?? [];
  const sentList = sentQuery.data?.list ?? [];
  const unreadCount = inboxList.filter((m) => !m.is_read).length;

  return (
    <div className="container-base py-10">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-3xl font-extrabold">쪽지</h1>
          <p className="mt-2 text-sm text-[var(--color-text-sub)]">
            {unreadCount > 0 ? (
              <>
                읽지 않은 쪽지가{' '}
                <strong className="text-[var(--color-primary)]">{unreadCount}건</strong> 있어요.
              </>
            ) : (
              '다른 유저와 1:1 로 대화할 수 있어요.'
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {tab === 'inbox' && unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => readAllMutation.mutate()}
              loading={readAllMutation.isPending}
            >
              <Check className="h-4 w-4" />
              모두 읽음
            </Button>
          )}
          <Button size="sm" onClick={() => setComposeOpen((v) => !v)}>
            {composeOpen ? <X className="h-4 w-4" /> : <PenSquare className="h-4 w-4" />}
            {composeOpen ? '닫기' : '쪽지 보내기'}
          </Button>
        </div>
      </header>

      {/* 쪽지 작성 폼 */}
      {composeOpen && (
        <div className="mb-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="mb-3 text-sm font-bold">쪽지 보내기</h3>

          {/* 받는 사람 */}
          {receiver ? (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-[var(--color-bg)] px-3 py-2">
              <Avatar src={receiver.profile_image_url ?? undefined} alt={receiver.nickname} size="sm" />
              <span className="font-semibold">{receiver.nickname}</span>
              <LevelBadge level={receiver.level} size="sm" />
              <button
                onClick={() => { setReceiver(null); setUserQuery(''); }}
                className="ml-auto text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div ref={userWrapRef} className="relative mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-sub)]" />
                <Input
                  value={userQuery}
                  onChange={(e) => { setUserQuery(e.target.value); setUserDropOpen(true); }}
                  onFocus={() => setUserDropOpen(true)}
                  placeholder="받는 사람 닉네임 검색..."
                  className="pl-9"
                />
              </div>
              {userDropOpen && (userResults?.list?.length ?? 0) > 0 && (
                <ul className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
                  {userResults!.list.map((u) => (
                    <li key={u.user_id}>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setReceiver(u);
                          setUserQuery('');
                          setUserDropOpen(false);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--color-bg)]"
                      >
                        <Avatar src={u.profile_image_url ?? undefined} alt={u.nickname} size="sm" />
                        <span className="font-medium">{u.nickname}</span>
                        <LevelBadge level={u.level} size="sm" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* 내용 */}
          <Textarea
            value={msgContent}
            onChange={(e) => setMsgContent(e.target.value)}
            placeholder="쪽지 내용을 입력하세요 (최대 1000자)"
            maxLength={1000}
            rows={3}
            className="mb-3 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm placeholder:text-[var(--color-text-sub)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40"
          />

          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-sub)]">
              {msgContent.length}/1000
            </span>
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={!receiver || !msgContent.trim()}
              loading={sendMutation.isPending}
            >
              <Send className="h-4 w-4" />
              보내기
            </Button>
          </div>
        </div>
      )}

      {/* 탭 */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setTab('inbox')}
          className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
            tab === 'inbox'
              ? 'bg-[var(--color-primary)] text-white'
              : 'bg-[var(--color-bg)] text-[var(--color-text-sub)]'
          }`}
        >
          <Mail className="h-3.5 w-3.5" />
          받은 쪽지
          {unreadCount > 0 && (
            <span className="ml-0.5 rounded-full bg-white/20 px-1.5 text-[10px]">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('sent')}
          className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
            tab === 'sent'
              ? 'bg-[var(--color-primary)] text-white'
              : 'bg-[var(--color-bg)] text-[var(--color-text-sub)]'
          }`}
        >
          <Send className="h-3.5 w-3.5" />
          보낸 쪽지
        </button>
      </div>

      {/* 로딩 */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      )}

      {/* 받은 쪽지 */}
      {tab === 'inbox' && !isLoading && (
        <ul className="space-y-2">
          {inboxList.map((m) => (
            <li
              key={m.id}
              onClick={() => !m.is_read && markReadMutation.mutate(m.id)}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors',
                m.is_read
                  ? 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-primary)]/40'
                  : 'border-[var(--color-primary)]/30 bg-[var(--color-primary)]/[0.04] hover:border-[var(--color-primary)]/60',
              )}
            >
              <Avatar src={m.sender_image ?? undefined} alt={m.sender_nickname} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="max-w-[60%] truncate font-semibold">{m.sender_nickname}</span>
                  <LevelBadge level={m.sender_level} size="sm" />
                  <span className="ml-auto shrink-0 text-xs text-[var(--color-text-sub)]">
                    {timeAgo(m.created_at)}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-[var(--color-text-sub)]">{m.content}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {!m.is_read && (
                  <span className="h-2 w-2 rounded-full bg-[var(--color-primary)]" />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteMutation.mutate(m.id);
                  }}
                  className="rounded-lg p-1.5 text-[var(--color-text-sub)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-fall)]"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
          {inboxList.length === 0 && (
            <EmptyState
              icon={Inbox}
              title="받은 쪽지가 없어요"
              description="다른 유저가 보낸 쪽지가 여기에 쌓여요."
            />
          )}
        </ul>
      )}

      {/* 보낸 쪽지 */}
      {tab === 'sent' && !isLoading && (
        <ul className="space-y-2">
          {sentList.map((m) => (
            <li
              key={m.id}
              className="flex items-start gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition-colors hover:border-[var(--color-primary)]/40"
            >
              <Avatar src={m.receiver_image ?? undefined} alt={m.receiver_nickname} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="max-w-[60%] truncate font-semibold">{m.receiver_nickname}</span>
                  <LevelBadge level={m.receiver_level} size="sm" />
                  <span className="ml-auto flex shrink-0 items-center gap-1 text-xs text-[var(--color-text-sub)]">
                    {m.is_read ? (
                      <MailOpen className="h-3 w-3" />
                    ) : (
                      <Mail className="h-3 w-3" />
                    )}
                    {m.is_read ? '읽음' : '안읽음'}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-[var(--color-text-sub)]">{m.content}</p>
                <span className="mt-0.5 text-[10px] text-[var(--color-text-sub)]">{timeAgo(m.created_at)}</span>
              </div>
              <button
                onClick={() => deleteMutation.mutate(m.id)}
                className="shrink-0 rounded-lg p-1.5 text-[var(--color-text-sub)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-fall)]"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
          {sentList.length === 0 && (
            <EmptyState
              icon={Send}
              title="보낸 쪽지가 없어요"
              description="쪽지 보내기 버튼을 눌러 대화를 시작해 보세요."
            />
          )}
        </ul>
      )}
    </div>
  );
}
